import type { BasePagingResponse, BaseResponse } from "../../../lib/api-contract";
import { apiUtils } from "../../../utils/apiUtils";
import type {
  CreateSurveyRequest,
  SurveyMutationResponse,
  GetSurveyListForAdminRequest,
  GetSurveyStatisticsRequest,
  SurveyAdminDetailResponse,
  SurveyItemResponse,
  SurveyListItemForAdminResponse,
  SurveyPublishRequest,
  SurveyPublishResponse,
  SurveyRepository,
  SurveyStatisticsResponse,
  SurveySummaryData,
  SurveySummaryResponse,
  UpdateSurveyRequest,
} from "../types/survey.types";

export async function getSurveyList(
  request: GetSurveyListForAdminRequest,
): Promise<BasePagingResponse<SurveyListItemForAdminResponse>> {
  return apiUtils.getData<BasePagingResponse<SurveyListItemForAdminResponse>>(
    "/v1/portal/survey",
    "Failed to fetch surveys",
    { params: request },
  );
}

export function deleteSurvey(id: string): Promise<BaseResponse<object>> {
  return apiUtils.delete<object>(`/v1/portal/survey/${id}`);
}

export async function createSurvey(request: CreateSurveyRequest): Promise<SurveyMutationResponse> {
  const response = await apiUtils.post<SurveyItemResponse, CreateSurveyRequest>(
    "/v1/portal/survey",
    request,
  );

  if (!response.data) throw new Error("Invalid API response structure");

  return {
    success: response.success,
    message: response.message ?? "",
    user_message: response.user_message,
    data: response.data,
  };
}

export async function updateSurvey(
  id: string,
  request: UpdateSurveyRequest,
): Promise<SurveyMutationResponse> {
  const response = await apiUtils.put<SurveyItemResponse, UpdateSurveyRequest>(
    `/v1/portal/survey/${id}`,
    request,
  );

  if (!response.data) throw new Error("Invalid API response structure");

  return {
    success: response.success,
    message: response.message ?? "",
    user_message: response.user_message,
    data: response.data,
  };
}

export async function getSurveyDetail(id: string): Promise<SurveyAdminDetailResponse> {
  return apiUtils.getData<SurveyAdminDetailResponse>(
    `/v1/portal/survey/${id}`,
    "Failed to fetch survey detail",
  );
}

export async function publishSurvey(
  id: string,
  request: SurveyPublishRequest,
): Promise<SurveyPublishResponse> {
  const response = await apiUtils.put<SurveyItemResponse, SurveyPublishRequest>(
    `/v1/portal/survey/${id}/publish`,
    request,
  );

  if (!response.data) throw new Error("Invalid API response structure");

  return {
    success: response.success,
    message: response.message ?? "",
    user_message: response.user_message,
    data: response.data,
  };
}

export async function getSurveySummary(id: string): Promise<SurveySummaryResponse> {
  const response = await apiUtils.get<SurveySummaryData>(`/v1/portal/survey/summary/${id}`);

  if (!response.data) throw new Error("Invalid API response structure");

  return {
    success: response.success,
    http_request_status: response.http_request_status,
    message: response.message ?? "",
    user_message: response.user_message,
    data: response.data,
  };
}

export function getSurveyStatistics(
  request: GetSurveyStatisticsRequest,
  signal?: AbortSignal,
): Promise<SurveyStatisticsResponse> {
  return apiUtils.getData<SurveyStatisticsResponse>(
    "/v1/portal/survey/statistic",
    "Failed to fetch survey statistics",
    { params: request, signal },
  );
}

export const surveyApi: SurveyRepository = {
  getSurveyList,
  deleteSurvey,
  createSurvey,
  updateSurvey,
  getSurveyDetail,
  publishSurvey,
  getSurveySummary,
  getSurveyStatistics,
};
